import { replaceInPageHistoryUrl } from "@/main/navigation/NavigationHistoryState";

// Resume-an-intended-group-join helper.
//
// A logged-out visitor who clicks "Join" on a group is gated behind account-access; the intended join
// is carried as a `?join=1` continuation to the group's own page (set by GroupButtons for the login
// path, and by the verification-email link for the signup path). On arrival, GroupButtons reads the
// param and auto-completes the join. This mirrors the notifications-continuation pattern
// (`openNotifications=1`) in AccountAccessContinuation.ts. See
// designs/04-live/2026-06-20-group-join-resume-after-auth-design.md.

const JOIN_PARAM = "join";

function stripTrailingSlashes(path: string): string {
    return path.replace(/\/+$/, "");
}

/**
 * Returns true when the current URL carries a `?join=1` intent for THIS group's page, after stripping
 * the param from the address bar (so a re-render / back-nav does not re-trigger the join). The
 * slug-in-path guard means a stray param on a multi-card directory route never acts on the wrong
 * group. No-op (returns false) on the server or when the param/group does not match.
 */
export function consumeGroupJoinIntent(groupSlug: string): boolean {
    if (typeof window === "undefined" || !groupSlug) {
        return false;
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get(JOIN_PARAM) !== "1") {
        return false;
    }

    if (stripTrailingSlashes(url.pathname) !== `/group/${groupSlug}`) {
        return false;
    }

    url.searchParams.delete(JOIN_PARAM);
    const cleaned = `${url.pathname}${url.search}${url.hash}`;
    replaceInPageHistoryUrl(cleaned, "consume-one-shot");
    return true;
}

/** Builds the same-site relative continuation URL that resumes a join on the group's own page. */
export function buildGroupJoinContinuationUrl(groupSlug: string): string {
    return `/group/${groupSlug}?${JOIN_PARAM}=1`;
}

const WELCOME_PARAM = "welcome";

/**
 * Returns true when the current URL carries a `?welcome=1` marker for THIS group's page, after
 * stripping the param from the address bar.
 *
 * This is the private-group trigger for the new-joiner welcome dialog: the group-approval
 * notification's deep link (`GroupRequestJoinApprovedNotification.computeUrl()`) carries the marker,
 * so clicking it means the user is arriving *because* they were just let in. That one-time event is
 * what lets the greeting exist without any stored "was this person welcomed?" state — see
 * designs/01-now/2026-07-21-group-new-joiner-welcome-technical-design.md §4b.
 *
 * Unlike the join intent, the path guard accepts EITHER the slug or the public id, because the
 * notification builds a public-id URL (`/group/{groupPublicId}`) while most in-app links are
 * slug-based and the group page resolves both.
 */
export function consumeGroupWelcomeIntent(groupSlug: string, groupPublicId?: string | null): boolean {
    if (typeof window === "undefined" || (!groupSlug && !groupPublicId)) {
        return false;
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get(WELCOME_PARAM) !== "1") {
        return false;
    }

    const path = stripTrailingSlashes(url.pathname);
    const matchesThisGroup =
        (!!groupSlug && path === `/group/${groupSlug}`) ||
        (!!groupPublicId && path === `/group/${groupPublicId}`);
    if (!matchesThisGroup) {
        return false;
    }

    url.searchParams.delete(WELCOME_PARAM);
    const cleaned = `${url.pathname}${url.search}${url.hash}`;
    replaceInPageHistoryUrl(cleaned, "consume-one-shot");
    return true;
}
